import React, { useState } from "react";
import axios from "axios";
import { Phone, Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL2, SERVICES } from "../../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildMailto = (form) => {
  const subject = encodeURIComponent(`Quote Request from ${form.name}`);
  const body = encodeURIComponent(
    `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email}\nService: ${form.service}\n\nMessage:\n${form.message}`
  );
  return `mailto:${EMAIL},${EMAIL2}?subject=${subject}&body=${body}`;
};

export const ContactForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        service: form.service || null,
        message: form.message || null,
      });
      if (!data.emailed) {
        window.location.href = buildMailto(form);
      }
      setDone(true);
      toast.success("Request sent! We'll reach out shortly.");
    } catch (err) {
      window.location.href = buildMailto(form);
      toast.message("Opening your email app to send us the details.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full h-14 px-4 border-2 border-[#0E2A56] bg-white text-[#14161A] font-medium text-lg placeholder:text-slate-400 focus:outline-none focus:border-[#C8102E]";

  return (
    <section id="quote" data-testid="quote-section" className="py-20 md:py-28 bg-[#0A1E3F]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="font-heading font-bold uppercase tracking-[0.2em] text-[#C8102E] text-sm">
            Free Estimate
          </p>
          <h2 className="mt-2 font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white">
            Get Your Free Quote Today
          </h2>
          <p className="mt-4 text-lg text-white/80 font-medium max-w-lg">
            Tell us about your project and we'll get right back to you. Need help now?
            Call us directly for the fastest response.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone-link" className="flex items-center gap-4 text-white group">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-[#C8102E]">
                <Phone className="h-6 w-6" strokeWidth={2.5} />
              </span>
              <span>
                <span className="block text-sm text-white/60 uppercase tracking-wide font-heading">Call or Text</span>
                <span className="block font-heading text-2xl font-bold group-hover:text-[#C8102E] transition-colors">{PHONE_DISPLAY}</span>
              </span>
            </a>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="flex items-center gap-4 text-white group break-all">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-[#0E2A56] border-2 border-white/20">
                <Mail className="h-6 w-6" strokeWidth={2.5} />
              </span>
              <span>
                <span className="block text-sm text-white/60 uppercase tracking-wide font-heading">Email Us</span>
                <span className="block font-semibold group-hover:text-[#C8102E] transition-colors">{EMAIL}</span>
                <span className="block font-semibold group-hover:text-[#C8102E] transition-colors">{EMAIL2}</span>
              </span>
            </a>
          </div>
        </div>

        {done ? (
          <div data-testid="quote-success" className="bg-white border-4 border-[#C8102E] p-10 text-center">
            <CheckCircle2 className="h-16 w-16 text-[#0E2A56] mx-auto" strokeWidth={2} />
            <h3 className="mt-4 font-heading text-3xl font-extrabold uppercase text-[#0E2A56]">Thank You!</h3>
            <p className="mt-2 text-slate-700 text-lg">
              We received your request and will contact you shortly. For urgent needs, call{" "}
              <a href={`tel:${PHONE_TEL}`} className="font-bold text-[#C8102E]">{PHONE_DISPLAY}</a>.
            </p>
          </div>
        ) : (
          <form data-testid="quote-form" onSubmit={onSubmit} className="bg-white border-4 border-white p-6 sm:p-8 space-y-4 shadow-hard-red">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="form-name" className={inputCls} placeholder="Your Name*" value={form.name} onChange={update("name")} />
              <input data-testid="form-phone" className={inputCls} placeholder="Phone Number*" value={form.phone} onChange={update("phone")} />
            </div>
            <input data-testid="form-email" type="email" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={update("email")} />
            <select data-testid="form-service" className={inputCls} value={form.service} onChange={update("service")}>
              <option value="">Select a Service</option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.title}>{s.title}</option>
              ))}
              <option value="Other / General Repair">Other / General Repair</option>
            </select>
            <textarea data-testid="form-message" rows={4} className="w-full px-4 py-3 border-2 border-[#0E2A56] bg-white text-[#14161A] font-medium text-lg placeholder:text-slate-400 focus:outline-none focus:border-[#C8102E]" placeholder="Tell us about your project" value={form.message} onChange={update("message")} />
            <button
              data-testid="form-submit"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-3 bg-[#C8102E] hover:bg-[#9E0C24] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wide text-lg py-4 transition-colors"
            >
              <Send className="h-5 w-5" strokeWidth={2.5} />
              {submitting ? "Sending..." : "Request My Free Quote"}
            </button>
            <p className="text-center text-sm text-slate-500">Or call us now at{" "}
              <a href={`tel:${PHONE_TEL}`} className="font-bold text-[#0E2A56]">{PHONE_DISPLAY}</a>
            </p>
          </form>
        )}
      </div>
    </section>
  );
};
